import { LucideIcon } from "lucide-react";
import { PREMIUM_FEATURE_KEYS } from "@/shared/components/UpsellPage/configs/upgradeFeatures.config";
import type { PlanTier } from "@/shared/components/UpsellPage/types/upsellPage.types";
import {
  ROUTE_REGISTRY, checkFeatureRequirement, checkRouteReadPermission, PARENT_ROUTES,
} from "@/shared/configs/routeRegistry.config";
import type { RouteFeatureRequirement } from "@/shared/configs/routeRegistry.config";
import type { UpsellCategoryKey } from "@/shared/types/common";
import { PlanFeatures } from "@/shared/types/planFeatures";
import type { RepresentativePermissions } from "@/shared/types/representativePermissions";
import { getContentFeatureAvailability, isContentFeatureAvailable } from "@/shared/utils/contentFeatureAvailability.utils";
import { getUpsellCategoryFromRoute } from "@/shared/utils/upsell.utils";

export type NavigationSubItem = {
  name: string
  url: string
  icon?: LucideIcon
  locked?: boolean
  requiredTier?: PlanTier
  activePaths?: string[]
  upsellCategory?: UpsellCategoryKey
};

export type NavigationItem = {
  name: string
  url: string
  icon: LucideIcon
  isCollapsible?: boolean
  hasCustomSubContent?: boolean
  items?: NavigationSubItem[]
  locked?: boolean
  requiredTier?: PlanTier
  upsellCategory?: UpsellCategoryKey
};

/**
 * Determine the minimum tier required for a given feature requirement.
 */
const getRequiredTier = (requirement?: RouteFeatureRequirement): PlanTier => {
  if (!requirement) return "standard";

  if (typeof requirement === "string") {
    return PREMIUM_FEATURE_KEYS.has(requirement as never) ? "premium" : "standard";
  }

  if ("all" in requirement) {
    return requirement.all.some((f) => PREMIUM_FEATURE_KEYS.has(f as never))
      ? "premium"
      : "standard";
  }

  if ("any" in requirement) {
    return requirement.any.some((f) => PREMIUM_FEATURE_KEYS.has(f as never))
      ? "premium"
      : "standard";
  }

  if ("feature" in requirement) {
    return PREMIUM_FEATURE_KEYS.has(requirement.feature as never)
      ? "premium"
      : "standard";
  }

  return "standard";
};

/**
 * Get the navigation items for the routes that are accessible with the given features (plan features) for the current organization.
 * When upsell_promotion is enabled, inaccessible items are included but marked as locked.
 * @param features - The features to check the requirement for the routes to be displayed
 * @returns The navigation config
 */
export const getNavigationItems = (
  features: Partial<PlanFeatures>,
  permissions?: RepresentativePermissions | null,
): { navigation: NavigationItem[] } => {
  const isUpsellEnabled = !!features.upsell_promotion;
  const navigationItems: Array<NavigationItem & { _order: number }> = [];
  const parentItemsMap: Record<string, NavigationSubItem[]> = {};

  Object.entries(ROUTE_REGISTRY).forEach(([key, route]) => {
    if (!route.navigation) return;
    if (typeof route.feature === "string" && !isContentFeatureAvailable(route.feature, getContentFeatureAvailability(features))) return;

    const hasAccess = checkFeatureRequirement(route.feature, features);

    if (!hasAccess && !isUpsellEnabled) {
      return;
    }

    const hasReadPermission = checkRouteReadPermission(route, permissions ?? null);
    if (hasAccess && !hasReadPermission) {
      return;
    }

    const isLocked = !hasAccess;
    const requiredTier = isLocked ? getRequiredTier(route.feature) : undefined;
    const upsellCategory = getUpsellCategoryFromRoute(route);

    if (route.navigation.parent) {
      const parentId = route.navigation.parent;
      const items = parentItemsMap[parentId] ?? [];
      items.push({
        name: route.navigation.label,
        url: route.path,
        icon: route.navigation.icon,
        locked: isLocked,
        requiredTier,
        activePaths: route.navigation.activePaths,
        upsellCategory,
      });
      parentItemsMap[parentId] = items;
    }
    else {
      navigationItems.push({
        name: route.navigation.label,
        url: route.path,
        icon: route.navigation.icon,
        isCollapsible: key === "fileManager",
        hasCustomSubContent: key === "fileManager",
        items: key === "fileManager" ? [] : undefined,
        locked: isLocked,
        requiredTier,
        upsellCategory,
        _order: route.navigation.order ?? 999,
      });
    }
  });

  Object.entries(PARENT_ROUTES).forEach(([parentId, parentConfig]) => {
    const items = parentItemsMap[parentId];
    if (items && items.length > 0) {
      const firstAccessibleChild = items.find((item) => !item.locked);
      const firstChild = Object.values(ROUTE_REGISTRY).find(
        (r) => r.navigation?.parent === parentId,
      );
      const allChildrenLocked = items.every((item) => item.locked);
      // Use the highest tier required among children
      const highestTier = items.some((item) => item.requiredTier === "premium")
        ? "premium" as const
        : "standard" as const;
      // When the parent is fully locked, route to a child whose tier matches
      // the badge so the upsell page reflects the displayed tier (e.g. a
      // premium badge must land on a premium-locked child, not the first
      // standard child by registry order).
      const firstLockedChildOfHighestTier = allChildrenLocked
        ? items.find((item) => item.requiredTier === highestTier)
        : undefined;

      navigationItems.push({
        name: parentConfig.label,
        url: firstAccessibleChild?.url ?? firstLockedChildOfHighestTier?.url ?? firstChild?.path ?? "#",
        icon: parentConfig.icon,
        isCollapsible: true,
        items,
        locked: allChildrenLocked,
        requiredTier: allChildrenLocked ? highestTier : undefined,
        upsellCategory: parentConfig.upsellCategory,
        _order: parentConfig.order,
      });
    }
  });

  navigationItems.sort((a, b) => a._order - b._order);

  const navigation: NavigationItem[] = navigationItems.map(({ _order, ...item }) => item);

  return { navigation };
};
